import { memo } from 'react';

import { Checkbox as DesignSystemCheckbox } from 'design-system';
import { cn } from '@/lib/utils';

export interface CustomCheckboxProps extends Omit<
  React.ComponentProps<typeof DesignSystemCheckbox>,
  'checked' | 'onCheckedChange'
> {
  checked?: boolean;
  defaultChecked?: boolean;
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Shows a disabled lock-style square while preserving the selected state visually. */
  modeLocked?: boolean;
  /** Optional callback for clicks on the lock overlay when modeLocked is true. */
  onLockedClick?: () => void;
}

const CustomCheckbox = ({
  checked,
  defaultChecked,
  indeterminate = false,
  modeLocked = false,
  onCheckedChange,
  onLockedClick,
  className,
  disabled,
  ...props
}: CustomCheckboxProps) => {
  const checkbox = (
    <DesignSystemCheckbox
      checked={checked}
      className={cn(modeLocked && 'opacity-100!', className)}
      defaultChecked={defaultChecked}
      disabled={disabled || modeLocked}
      indeterminate={indeterminate}
      onCheckedChange={nextChecked => onCheckedChange?.(nextChecked)}
      {...props}
    />
  );

  if (!modeLocked) return checkbox;

  return (
    <span className='relative inline-flex'>
      {checkbox}
      <button
        aria-label='Change locked selection'
        className='absolute inset-0 z-10 cursor-not-allowed bg-gx-text-disabled'
        data-testid='mode-lock-overlay'
        type='button'
        onClick={event => {
          event.stopPropagation();
          onLockedClick?.();
        }}
      />
    </span>
  );
};

export default memo(CustomCheckbox);
